import React, {memo, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import styled from 'styled-components';
import {ControlButton, TscButtonType, Glyphs, Tabs, TscThemeName, TscThemeNames, TscStyles} from '@techsmith/tsc-cloud-style-guide';
import cssConstants from '../../constants/cssConstants';
import config from '../../service/config';
import {NavigationTabTypes} from '../../constants/Constants';
import {ITab} from '../../model/tab';
import {useTscTheme} from '../../context/themeProvider';

export const TabsGrid = styled.div`
   display: grid;
   grid-template-areas:
      'leftarrow tabs rightarrow';
   grid-template-columns: max-content 1fr max-content;
   width: 100%;
   color: ${TscStyles.color.text.mediumDark};
`;

const LeftArrowContainer = styled.div`
   grid-area: leftarrow;
   display: flex;
   justify-content: center;
   align-items: center;
`;

const RightArrowContainer = styled.div`
   grid-area: rightarrow;
   display: flex;
   justify-content: center;
   align-items: center;
`;

const TabsContainer = styled.div`
   grid-area: tabs;
   overflow: hidden;
`;

const TabWrapper = styled.nav<{theme: TscThemeNames}>`
   max-width: ${cssConstants.contentWidth};
   overflow-x: auto;
   overflow-y: hidden;
   scrollbar-width: none;

   &::-webkit-scrollbar {
      display: none;
   }

   & {
      -ms-overflow-style: none;
      overflow: -moz-scrollbars-none;
   }

   div {
      height: 2.5rem;
      box-sizing: border-box;

      a {
         /* !important is here because tsc-styleguide will win with 0 for the 'last-child' otherwise, could alternatively fight specificity if this ever becomes a problem */
         flex: none;
         margin: 0.125rem ${cssConstants.viewPageMeasurements.managementTabHorizontalMargin} 0 ${cssConstants.viewPageMeasurements.managementTabHorizontalMargin} !important;
         padding: 0 ${cssConstants.viewPageMeasurements.managementTabHorizontalPadding};
         outline-offset: -${cssConstants.viewPageMeasurements.managementTabHorizontalPadding};

         &::after {
            margin: 0 .125rem;
         }
      }

      span {
         font-size: 0.825rem;
         font-weight: 600;
         color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.mediumDark};
      }
   }
`;

const ButtonWrapper = styled.div`
   padding: 0 0.25rem;
`;

export const MediaTabsHeaderBase: React.FC<IMediaTabsHeaderProps> = ({tabs}) => {
   const [displayLeftArrow, setDisplayLeftArrow] = useState(false);
   const [displayRightArrow, setDisplayRightArrow] = useState(window.innerWidth < (config.watchConfig?.isOwnerOrAdmin && !config.environmentData.IsLti ? cssConstants.adminTabsBarBreakpoint : config.watchConfig?.isOwnerOrAdmin && config.environmentData.IsLti ? cssConstants.ltiTabsBarBreakpoint : cssConstants.viewerTabsBarBreakpoint));
   const {t} = useTranslation();
   const theme = useTscTheme();
   const tabsElement = useRef<HTMLDivElement>(null);

   const updateOverflowArrows = () => {
      const tabsScrollable = tabsElement.current;
      const tolerance = 5;

      if (tabsScrollable.scrollWidth - tabsScrollable.offsetWidth - tabsScrollable.scrollLeft < tolerance) {
         setDisplayRightArrow(false);
      } else {
         setDisplayRightArrow(true);
      }

      if (tabsScrollable.scrollLeft < tolerance) {
         setDisplayLeftArrow(false);
      } else {
         setDisplayLeftArrow(true);
      }
   };

   const rightArrowOnClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      const tabsScrollable = tabsElement.current;
      const scrollLeft = Math.min(tabsScrollable.scrollLeft + tabsScrollable.offsetWidth, tabsScrollable.scrollWidth - tabsScrollable.offsetWidth);
      tabsScrollable.scrollTo({left: scrollLeft, behavior: 'smooth'});
   };

   const leftArrowOnClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      const tabsScrollable = tabsElement.current;
      const scrollLeft = Math.max(0, tabsScrollable.scrollLeft - tabsScrollable.offsetWidth);
      tabsScrollable.scrollTo({left: scrollLeft, behavior: 'smooth'});
   };

   useEffect(() => {
      const tabsElementHandle = tabsElement.current;
      tabsElement.current.addEventListener('scroll', updateOverflowArrows);
      return () => {
         tabsElementHandle.removeEventListener('scroll', updateOverflowArrows);
      };
   }, []);

   useEffect(() => {
      if (!tabsElement.current) {
         // eslint-disable-next-line no-undefined
         return undefined; // useEffect prefers undefined (or empty) _not_ null if exiting early like this
      }
      // this is required because we cant use pure-css to determine the size of the tab content currently rendering as a child when trying to set 'bottom'
      // value for the sticky offset.  if we refactored to put media-tab-header and media-tab-content each in it's own grid container... maybe we do with pure css
      const observer = new ResizeObserver(() => {
         updateOverflowArrows();
      });
      observer.observe(tabsElement.current);
      return () => {
         observer.disconnect();
      };
   }, []);

   return (
      <TabsGrid>
         <LeftArrowContainer>
            {displayLeftArrow && (
               <ButtonWrapper>
                  <ControlButton
                     buttonType={TscButtonType.floating}
                     glyph={<Glyphs.ArrowLeft16x16 />}
                     themeName={theme}
                     title={t('ScrollLeft')}
                     onClick={leftArrowOnClick}
                  />
               </ButtonWrapper>
            )}
         </LeftArrowContainer>
         <TabsContainer>
            <TabWrapper
               theme={theme}
               ref={tabsElement}
               id="tab-wrapper"
            >
               <Tabs
                  isAnchor={true}
                  tabs={tabs}
                  themeName={theme}
               />
            </TabWrapper>
         </TabsContainer>
         <RightArrowContainer>
            {displayRightArrow && (
               <ButtonWrapper>
                  <ControlButton
                     buttonType={TscButtonType.floating}
                     glyph={<Glyphs.ArrowRight16x16 />}
                     themeName={theme}
                     title={t('ScrollRight')}
                     onClick={rightArrowOnClick}
                  />
               </ButtonWrapper>
            )}
         </RightArrowContainer>
      </TabsGrid>
   );
};

export interface IMediaTabsHeaderProps {
   tabs: ITab[];
}

export interface INavigationTab extends ITab {
   which: NavigationTabTypes;
}

export default memo(MediaTabsHeaderBase);
